// src/pages/DataCenterSentry.jsx
// DataCenter Sentry - AI-Powered Autonomous Facility Management
// Edge AI Platform for Data Center Operations
// MWC Barcelona Demo - Celerway Xm

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK DATA CENTER DATA - Simulates autonomous facility management
// ════════════════════════════════════════════════════════════════════════════

const MOCK_FACILITY_GATEWAY = {
  id: 'gateway-lon1-dc',
  name: 'LON1 Edge Gateway',
  location: 'London Docklands DC',
  status: 'Online',
  assetCount: 12,
  activeAlerts: 4,
  stratusModel: 'Xm 5G Industrial',
  connectivity: '5G + Fiber',
  lastSync: new Date().toISOString(),
  pue: 1.42,  // Power Usage Effectiveness
};

const MOCK_ASSETS = [
  // Server Racks
  {
    id: 'RACK-A1-U42',
    type: 'Server Rack',
    category: 'compute',
    location: 'Row A, Position 1',
    status: 'operational',
    metrics: {
      cpuTemp: 78,
      avgLoad: 67,
      powerDraw: 8.2,  // kW
      powerCapacity: 12,
      servers: 14,
      serversOnline: 14,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'RACK-A2-U42',
    type: 'Server Rack',
    category: 'compute',
    location: 'Row A, Position 2',
    status: 'warning',
    metrics: {
      cpuTemp: 92,  // HIGH - trigger alert
      avgLoad: 94,  // HIGH
      powerDraw: 11.1,
      powerCapacity: 12,
      servers: 16,
      serversOnline: 16,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'RACK-B1-U42',
    type: 'Server Rack',
    category: 'compute',
    location: 'Row B, Position 1',
    status: 'operational',
    metrics: {
      cpuTemp: 71,
      avgLoad: 45,
      powerDraw: 6.4,
      powerCapacity: 12,
      servers: 12,
      serversOnline: 12,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'RACK-B2-U42',
    type: 'Server Rack',
    category: 'compute',
    location: 'Row B, Position 2',
    status: 'operational',
    metrics: {
      cpuTemp: 74,
      avgLoad: 52,
      powerDraw: 7.1,
      powerCapacity: 12,
      servers: 14,
      serversOnline: 13,  // One server offline
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  // Cooling Units
  {
    id: 'CRAC-01',
    type: 'Cooling Unit',
    category: 'cooling',
    location: 'Row A End',
    status: 'operational',
    metrics: {
      supplyTemp: 18,
      returnTemp: 28,
      airflow: 95,  // % of capacity
      humidity: 45,
      setpoint: 18,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'CRAC-02',
    type: 'Cooling Unit',
    category: 'cooling',
    location: 'Row B End',
    status: 'degraded',
    metrics: {
      supplyTemp: 21,  // Above setpoint
      returnTemp: 32,
      airflow: 58,  // LOW - trigger alert
      humidity: 52,
      setpoint: 18,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  // Power Systems
  {
    id: 'UPS-A',
    type: 'UPS System',
    category: 'power',
    location: 'Power Room A',
    status: 'operational',
    metrics: {
      load: 62,
      batteryHealth: 94,
      runtime: 18,  // minutes at current load
      inputVoltage: 415,
      outputVoltage: 415,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'UPS-B',
    type: 'UPS System',
    category: 'power',
    location: 'Power Room B',
    status: 'warning',
    metrics: {
      load: 71,
      batteryHealth: 23,  // LOW - trigger alert
      runtime: 8,
      inputVoltage: 414,
      outputVoltage: 415,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'PDU-ROW-A',
    type: 'PDU',
    category: 'power',
    location: 'Row A Distribution',
    status: 'warning',
    metrics: {
      load: 87,  // HIGH - trigger alert
      capacity: 100,
      circuits: 24,
      circuitsActive: 22,
      voltage: 230,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  {
    id: 'PDU-ROW-B',
    type: 'PDU',
    category: 'power',
    location: 'Row B Distribution',
    status: 'operational',
    metrics: {
      load: 54,
      capacity: 100,
      circuits: 24,
      circuitsActive: 18,
      voltage: 230,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  // Network
  {
    id: 'CORE-SW-01',
    type: 'Core Switch',
    category: 'network',
    location: 'Network Room',
    status: 'operational',
    metrics: {
      throughput: 78,  // Gbps
      capacity: 100,
      ports: 48,
      portsActive: 42,
      latency: 0.3,  // ms
      errors: 0,
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
  // Environmental
  {
    id: 'ENV-ZONE-A',
    type: 'Environmental Sensor',
    category: 'environmental',
    location: 'Zone A Floor',
    status: 'operational',
    metrics: {
      temperature: 22,
      humidity: 48,
      waterDetect: false,
      smokeDetect: false,
      particulates: 12,  // μg/m³
    },
    lastUpdate: '2025-01-31T14:58:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Data Center specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🚨', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  WARNING: { icon: '⚠️', text: 'WARNING', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  OPTIMIZING: { icon: '⚡', text: 'OPTIMIZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  NOMINAL: { icon: '✓', text: 'NOMINAL', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  OFFLINE: { icon: '○', text: 'OFFLINE', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const CATEGORY_COLORS = {
  compute: { bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', text: 'text-blue-300', icon: '🖥️' },
  cooling: { bg: 'from-cyan-400/30 to-teal-500/30', border: 'border-cyan-400/60', text: 'text-cyan-300', icon: '❄️' },
  power: { bg: 'from-amber-400/30 to-yellow-500/30', border: 'border-amber-400/60', text: 'text-amber-300', icon: '⚡' },
  network: { bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', text: 'text-purple-300', icon: '🌐' },
  environmental: { bg: 'from-green-400/30 to-emerald-500/30', border: 'border-green-400/60', text: 'text-green-300', icon: '🌡️' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Analyzes data center metrics
// ════════════════════════════════════════════════════════════════════════════

const detectDataCenterAnomalies = (assets) => {
  const anomalies = [];
  
  assets.forEach(asset => {
    const m = asset.metrics;
    
    // Server Rack anomalies
    if (asset.category === 'compute') {
      if (m.cpuTemp > 85) {
        anomalies.push({
          id: `temp-${asset.id}`,
          type: 'THERMAL_CRITICAL',
          assetId: asset.id,
          severity: m.cpuTemp > 90 ? 'critical' : 'warning',
          metric: `${m.cpuTemp}°C`,
          threshold: '85°C',
          description: `CPU temperature exceeds safe threshold`,
          recommendation: 'Migrate high-load VMs to cooler racks. Increase adjacent cooling output.',
          confidence: 96,
          action: {
            type: 'WORKLOAD_MIGRATION',
            target: 'RACK-B1-U42',
            description: 'Migrate 4 VMs to underutilized rack',
          },
        });
      }
      if (m.avgLoad > 90) {
        anomalies.push({
          id: `load-${asset.id}`,
          type: 'COMPUTE_OVERLOAD',
          assetId: asset.id,
          severity: 'warning',
          metric: `${m.avgLoad}%`,
          threshold: '90%',
          description: `Compute utilization critically high`,
          recommendation: 'Balance workloads across available racks or spin up overflow capacity.',
          confidence: 94,
          action: {
            type: 'LOAD_BALANCE',
            description: 'Redistribute workloads to adjacent racks',
          },
        });
      }
    }
    
    // Cooling anomalies
    if (asset.category === 'cooling') {
      if (m.airflow < 70) {
        anomalies.push({
          id: `airflow-${asset.id}`,
          type: 'COOLING_DEGRADED',
          assetId: asset.id,
          severity: m.airflow < 60 ? 'critical' : 'warning',
          metric: `${m.airflow}%`,
          threshold: '70%',
          description: `Airflow capacity degraded - potential filter blockage or fan failure`,
          recommendation: 'Increase output on adjacent CRAC units. Schedule immediate maintenance.',
          confidence: 91,
          action: {
            type: 'COOLING_COMPENSATE',
            target: 'CRAC-01',
            description: 'Increase CRAC-01 output to 100%',
          },
        });
      }
      if (m.supplyTemp > m.setpoint + 2) {
        anomalies.push({
          id: `supply-${asset.id}`,
          type: 'SUPPLY_TEMP_HIGH',
          assetId: asset.id,
          severity: 'warning',
          metric: `${m.supplyTemp}°C`,
          threshold: `${m.setpoint}°C`,
          description: `Supply air temperature above setpoint`,
          recommendation: 'Check refrigerant levels. Verify condenser operation.',
          confidence: 88,
        });
      }
    }
    
    // Power anomalies
    if (asset.type === 'UPS System') {
      if (m.batteryHealth < 30) {
        anomalies.push({
          id: `battery-${asset.id}`,
          type: 'BATTERY_DEGRADED',
          assetId: asset.id,
          severity: m.batteryHealth < 25 ? 'critical' : 'warning',
          metric: `${m.batteryHealth}%`,
          threshold: '30%',
          description: `UPS battery health critically low - runtime compromised`,
          recommendation: 'Schedule immediate battery replacement. Current runtime only supports brief outage.',
          confidence: 97,
          action: {
            type: 'MAINTENANCE_TICKET',
            priority: 'P1',
            description: 'Create emergency battery replacement ticket',
          },
        });
      }
    }
    
    if (asset.type === 'PDU') {
      if (m.load > 80) {
        anomalies.push({
          id: `pdu-${asset.id}`,
          type: 'PDU_OVERLOAD',
          assetId: asset.id,
          severity: m.load > 85 ? 'critical' : 'warning',
          metric: `${m.load}%`,
          threshold: '80%',
          description: `PDU load approaching capacity limit`,
          recommendation: 'Shed non-critical loads or redistribute to secondary PDU.',
          confidence: 95,
          action: {
            type: 'LOAD_SHED',
            description: 'Power down dev/test workloads on Row A',
          },
        });
      }
    }
    
    // Network anomalies
    if (asset.category === 'network') {
      if (m.errors > 0) {
        anomalies.push({
          id: `network-${asset.id}`,
          type: 'NETWORK_ERRORS',
          assetId: asset.id,
          severity: m.errors > 100 ? 'critical' : 'warning',
          metric: `${m.errors} errors`,
          threshold: '0 errors',
          description: `Network errors detected`,
          recommendation: 'Check cable integrity and switch port health.',
          confidence: 85,
        });
      }
    }
    
    // Environmental anomalies
    if (asset.category === 'environmental') {
      if (m.waterDetect) {
        anomalies.push({
          id: `water-${asset.id}`,
          type: 'WATER_DETECTED',
          assetId: asset.id,
          severity: 'critical',
          metric: 'WATER DETECTED',
          threshold: 'Dry',
          description: `Water leak detected in zone`,
          recommendation: 'Dispatch facilities team immediately. Isolate affected equipment.',
          confidence: 99,
        });
      }
    }
  });
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getAssetAIStatus = (asset, anomalies) => {
  const assetAnomalies = anomalies.filter(a => a.assetId === asset.id);
  
  if (asset.status === 'offline') return AI_STATUS.OFFLINE;
  
  const hasCritical = assetAnomalies.some(a => a.severity === 'critical');
  const hasWarning = assetAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical) return AI_STATUS.CRITICAL;
  if (hasWarning) return AI_STATUS.WARNING;
  if (asset.status === 'optimizing') return AI_STATUS.OPTIMIZING;
  return AI_STATUS.NOMINAL;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// NOC Command Node
const NOCCommandNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[240px] shadow-2xl shadow-cyan-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.facilityCount || 1}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Sites</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Assets</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.pue || '1.4'}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">PUE</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[10px] text-cyan-300 font-medium">AISentry™ Active</span>
      </div>
    </div>
  </div>
);

// Facility Gateway Node (Xm at data center)
const FacilityGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[260px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Assets</span>
            <div className="font-semibold text-blue-300">{data.assetCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Alerts</span>
            <div className={`font-semibold ${data.activeAlerts > 0 ? 'text-amber-300' : 'text-slate-300'}`}>{data.activeAlerts}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Infrastructure Asset Node
const AssetNode = ({ data }) => {
  const categoryStyle = CATEGORY_COLORS[data.category] || CATEGORY_COLORS.compute;
  const aiStatus = data.aiStatus || AI_STATUS.NOMINAL;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;

  const renderMetrics = () => {
    const m = data.metrics;
    
    switch (data.category) {
      case 'compute':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${m.cpuTemp > 85 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.cpuTemp > 85 ? 'text-red-300' : 'text-slate-300'}`}>{m.cpuTemp}°C</div>
              <div className="text-[8px] text-slate-500">TEMP</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.avgLoad > 90 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.avgLoad > 90 ? 'text-amber-300' : 'text-slate-300'}`}>{m.avgLoad}%</div>
              <div className="text-[8px] text-slate-500">LOAD</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.powerDraw}kW</div>
              <div className="text-[8px] text-slate-500">PWR</div>
            </div>
          </div>
        );
      case 'cooling':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${m.supplyTemp > m.setpoint + 2 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.supplyTemp > m.setpoint + 2 ? 'text-amber-300' : 'text-slate-300'}`}>{m.supplyTemp}°C</div>
              <div className="text-[8px] text-slate-500">SUPPLY</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.airflow < 70 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.airflow < 70 ? 'text-red-300' : 'text-slate-300'}`}>{m.airflow}%</div>
              <div className="text-[8px] text-slate-500">FLOW</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.humidity}%</div>
              <div className="text-[8px] text-slate-500">RH</div>
            </div>
          </div>
        );
      case 'power':
        if (data.type === 'UPS System') {
          return (
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-slate-300">{m.load}%</div>
                <div className="text-[8px] text-slate-500">LOAD</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${m.batteryHealth < 30 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${m.batteryHealth < 30 ? 'text-red-300' : 'text-slate-300'}`}>{m.batteryHealth}%</div>
                <div className="text-[8px] text-slate-500">BATT</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-slate-300">{m.runtime}m</div>
                <div className="text-[8px] text-slate-500">TIME</div>
              </div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${m.load > 80 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.load > 80 ? 'text-amber-300' : 'text-slate-300'}`}>{m.load}%</div>
              <div className="text-[8px] text-slate-500">LOAD</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.circuitsActive}/{m.circuits}</div>
              <div className="text-[8px] text-slate-500">CIRCUITS</div>
            </div>
          </div>
        );
      case 'network':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.throughput}G</div>
              <div className="text-[8px] text-slate-500">THRU</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.latency}ms</div>
              <div className="text-[8px] text-slate-500">LAT</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.portsActive}/{m.ports}</div>
              <div className="text-[8px] text-slate-500">PORTS</div>
            </div>
          </div>
        );
      case 'environmental':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.temperature}°C</div>
              <div className="text-[8px] text-slate-500">TEMP</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.humidity}%</div>
              <div className="text-[8px] text-slate-500">RH</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.waterDetect ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.waterDetect ? 'text-red-300' : 'text-emerald-300'}`}>
                {m.waterDetect ? 'LEAK!' : 'DRY'}
              </div>
              <div className="text-[8px] text-slate-500">WATER</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[170px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Asset header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-lg">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{data.id}</div>
            <div className="text-[10px] text-slate-400">{data.type}</div>
          </div>
        </div>
        
        {/* Location */}
        <div className="text-[10px] text-slate-500 mb-2">{data.location}</div>
        
        {/* Metrics */}
        {renderMetrics()}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  nocCommand: NOCCommandNode,
  facilityGateway: FacilityGatewayNode,
  asset: AssetNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, assets }) => {
  const [activeTab, setActiveTab] = useState('detections');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🏢</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AISentry™ Facility Monitor</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} detections • {assets.length} assets</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Autonomous Mode
          </div>
          <div className="text-[10px] text-slate-500">DCIM Integration Ready</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('detections')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'detections'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🎯 Detections
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'actions'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          ⚡ Actions
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'metrics'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📊 Metrics
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'detections' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All systems nominal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const asset = assets.find(a => a.id === anomaly.assetId);
                const categoryStyle = CATEGORY_COLORS[asset?.category] || CATEGORY_COLORS.compute;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.assetId}</div>
                          <div className="text-[10px] text-slate-400">{asset?.type}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">threshold: {anomaly.threshold}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1"
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'actions' && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg border border-cyan-500/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🔄</span>
                <div>
                  <div className="text-sm font-semibold text-white">Workload Migration</div>
                  <div className="text-[10px] text-slate-400">Automated Response</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-emerald-400">96%</div>
                  <div className="text-[10px] text-slate-500">Confidence</div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Action</div>
                <div className="text-sm text-cyan-300">Migrate 4 VMs from RACK-A2-U42 to RACK-B1-U42</div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Reason</div>
                <div className="text-xs text-slate-300">RACK-A2-U42 CPU temperature at 92°C exceeds safe threshold. RACK-B1-U42 has 45% load headroom and 71°C temps.</div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Impact</div>
                <div className="flex gap-4 text-xs">
                  <div>
                    <span className="text-slate-400">Downtime:</span>
                    <span className="text-emerald-300 ml-1">None (live migration)</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Duration:</span>
                    <span className="text-slate-300 ml-1">~8 min</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  ✓ Approve & Execute
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-500 pt-2">
              Powered by <span className="text-cyan-400">AISentry™</span>
            </div>
          </div>
        )}
        
        {activeTab === 'metrics' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Facility Overview</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-cyan-300">1.42</div>
                  <div className="text-xs text-slate-400">PUE (Power Usage)</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-emerald-300">98.7%</div>
                  <div className="text-xs text-slate-400">Uptime (30d)</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-amber-300">32.8</div>
                  <div className="text-xs text-slate-400">Total kW Draw</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-purple-300">22°C</div>
                  <div className="text-xs text-slate-400">Avg Floor Temp</div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Category Health</div>
              <div className="space-y-2">
                {Object.entries(CATEGORY_COLORS).map(([key, style]) => {
                  const categoryAssets = assets.filter(a => a.category === key);
                  const healthy = categoryAssets.filter(a => a.status === 'operational').length;
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{style.icon}</span>
                        <span className="text-sm text-slate-300 capitalize">{key}</span>
                      </div>
                      <span className={`text-sm font-medium ${healthy === categoryAssets.length ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {healthy}/{categoryAssets.length}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last scan: Just now</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT CARD
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, assets, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  const topAnomaly = criticalAnomalies[0];
  const asset = assets.find(a => a.id === topAnomaly.assetId);
  const categoryStyle = CATEGORY_COLORS[asset?.category] || CATEGORY_COLORS.compute;
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl animate-pulse">{categoryStyle.icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white mb-1">
              Facility Alert
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topAnomaly.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.assetId} • {asset?.location} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : topAnomaly.type.replace(/_/g, ' ')}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ assets, anomalies, pue, onOpenPanel }) => {
  const nominal = assets.filter(a => a.status === 'operational').length;
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-emerald-300 font-bold text-lg">{nominal}</span>
        <span className="text-slate-300 text-xs">/ {assets.length} Nominal</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{pue}</span>
        <span className="text-slate-300 text-xs">PUE</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">Alerts</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg px-4 py-2 border border-cyan-400/50 transition-colors"
      >
        <span className="text-cyan-300 text-sm">🤖</span>
        <span className="text-cyan-300 text-xs font-medium">AISentry™</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      {Object.entries(CATEGORY_COLORS).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-xs text-slate-300 capitalize">{key}</span>
        </div>
      ))}
    </div>
    <div className="pt-2 border-t border-slate-600/50 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase">AI Status</div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-red-500" />
        <span className="text-xs text-slate-300">Critical</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-amber-500" />
        <span className="text-xs text-slate-300">Warning</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-300">Nominal</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const DataCenterSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [assets] = useState(MOCK_ASSETS);
  const [gateway] = useState(MOCK_FACILITY_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectDataCenterAnomalies(assets);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 180;
    const ASSET_Y_START = 400;
    const CENTER_X = 600;
    
    // Group assets by category
    const categories = ['compute', 'cooling', 'power', 'network', 'environmental'];
    const assetsByCategory = {};
    categories.forEach(cat => {
      assetsByCategory[cat] = assets.filter(a => a.category === cat);
    });
    
    // 1. NOC Command (Cloud)
    newNodes.push({
      id: 'noc-command',
      type: 'nocCommand',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'NOC Command',
        sublabel: 'Network Operations Center',
        facilityCount: 1,
        assetCount: assets.length,
        pue: gateway.pue,
      },
    });
    
    // 2. Facility Gateway
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'facilityGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'noc-command',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#34d399', strokeWidth: 2 },
    });
    
    // 3. Assets by category in rows
    let currentY = ASSET_Y_START;
    const CATEGORY_GAP = 200;
    const ASSET_GAP = 190;
    
    categories.forEach(category => {
      const categoryAssets = assetsByCategory[category];
      if (categoryAssets.length === 0) return;
      
      const startX = CENTER_X - ((categoryAssets.length - 1) * ASSET_GAP) / 2;
      
      categoryAssets.forEach((asset, idx) => {
        const aiStatus = getAssetAIStatus(asset, detectedAnomalies);
        const assetId = `asset-${asset.id}`;
        
        newNodes.push({
          id: assetId,
          type: 'asset',
          position: { x: startX + idx * ASSET_GAP, y: currentY },
          data: { ...asset, aiStatus },
        });
        
        // Edge: Gateway → Asset
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
        const categoryStyle = CATEGORY_COLORS[category];
        const edgeColor = hasAlert 
          ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
          : categoryStyle.border.replace('border-', '').replace('/60', '');
        
        newEdges.push({
          id: `gateway-to-${assetId}`,
          source: `gateway-${gateway.id}`,
          target: assetId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: hasAlert ? edgeColor : '#22d3ee', strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [assets, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              AISentry™ — Autonomous Sovereign Edge™
            </h1>
            <p className="text-slate-300 text-sm mt-1">Autonomous Facility Management • Real-time Infrastructure Monitoring • Predictive Operations</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30">
                Edge AI Platform Demo
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar 
            assets={assets} 
            anomalies={anomalies}
            pue={gateway.pue}
            onOpenPanel={() => setIsAIPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveAlert 
        anomalies={anomalies} 
        assets={assets} 
        onViewDetails={() => setIsAIPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">AISentry™ Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to Facility Gateway</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'nocCommand') return '#22d3ee';
              if (node.type === 'facilityGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.WARNING) return '#f59e0b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        assets={assets}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default DataCenterSentry;
